import { Session } from '@benzinga/session';
import { getGlobalSession } from '../session';
import { DateTime } from 'luxon';
import {
  CalendarManager,
  Dividend,
  Earnings,
  sortByField,
  Splits,
  Guidance,
  Conference,
  ISetParams,
  AnalystRatingsSummary,
  getDetailedData as getRatingsDetailedData,
  MergersAndAcquisitions,
  generateMAText,
} from '@benzinga/calendar-manager';
import { QuotesManager, ShortInterest } from '@benzinga/quotes-manager';
import { InsidersTradeFiling, InsiderTradesManager } from '@benzinga/insider-trades-manager';
import isPast from 'date-fns/isPast';
import parseISO from 'date-fns/parseISO';
import { OptionChainManager, organizeOptionData, OrganizeOptionDataReturn } from '@benzinga/data-option-chain';
import { SafePromise } from '@benzinga/safe-await';

// ==================== Short Interest ====================

export interface ShortInterestSummary {
  last: ShortInterest | null;
  shortInterest: ShortInterest[];
}

export const fetchShortInterest = (session: Session, symbols: string[]) => {
  const QuoteManager = session.getManager(QuotesManager);
  const dateFrom = DateTime.fromJSDate(new Date()).minus({ months: 24 });
  const dateTo = DateTime.fromJSDate(new Date());

  const query = {
    dateFrom: dateFrom.toFormat('yyyy-MM-dd'),
    dateTo: dateTo.toFormat('yyyy-MM-dd'),
    symbols,
  };

  return QuoteManager.getShortInterest(query);
};

export const sortByRecentDate = (data: ShortInterest[]) => {
  return data.sort((a, b) => new Date(b.recordDate || 0).getTime() - new Date(a.recordDate || 0).getTime());
};

const getShortInterestDetailedData = (shortInterest: ShortInterest[]): ShortInterestSummary => {
  const sorted = sortByRecentDate(shortInterest);
  const latest = sorted[0];
  return {
    last: latest ?? null,
    shortInterest,
  };
};

export const buildShortInterestSummary = (shortInterest: ShortInterest[] | undefined): ShortInterestSummary => {
  return getShortInterestDetailedData(shortInterest ?? []);
};

export const getShortInterestSummary = async (symbol: string): Promise<ShortInterestSummary | null> => {
  const session = getGlobalSession();
  const shortInterestRes = await fetchShortInterest(session, [symbol]);
  const shortInterestResults = shortInterestRes.ok;
  if (!shortInterestResults) return null;
  const shortInterest = shortInterestResults[symbol.toUpperCase()]?.data;
  if (!shortInterest?.length) return null;
  return getShortInterestDetailedData(shortInterest);
};

// ==================== Dividends ====================

export interface DividendSummary {
  next: Dividend | null;
  last: Dividend | null;
  futureDividends: Dividend[];
  dividends: Dividend[];
  peersAverageDividend?: number;
  yearOverYearGrowth?: number | null;
  avgAnnualizedDividendGrowth?: number | null;
  largestDividendYield?: Dividend;
  sustainabilityData?: any;
}

export const fetchDividends = (symbol: string, session: Session, pageSize = 100) => {
  try {
    return session.getManager(CalendarManager).getCalendarData(
      'dividends',
      {
        pageSize,
        symbols: [symbol],
      },
      true,
    );
  } catch (error) {
    return { err: error, ok: [] };
  }
};

const compareExDividendDates = (left: Dividend, right: Dividend) =>
  parseISO(left.ex_dividend_date!).getTime() - parseISO(right.ex_dividend_date!).getTime();

const getFutureDividends = (dividends: Dividend[]) =>
  (dividends ?? [])
    .filter((dividend: Dividend) => dividend.ex_dividend_date && !isPast(parseISO(dividend.ex_dividend_date)))
    .sort(compareExDividendDates);

const getPastDividends = (dividends: Dividend[]) =>
  (dividends ?? [])
    .filter((dividend: Dividend) => dividend.ex_dividend_date && isPast(parseISO(dividend.ex_dividend_date)))
    .sort((left, right) => compareExDividendDates(right, left));

export const getNextDividendInfo = (dividends: Dividend[]) => {
  const futureDividends = getFutureDividends(dividends);
  return futureDividends?.[0] ?? null;
};

export const getLastDividendInfo = (dividends: Dividend[]) => getPastDividends(dividends)[0] ?? null;

const getCompletedDividendsByExDate = (dividends: Dividend[]) =>
  (dividends ?? [])
    .filter((dividend: Dividend) => dividend.frequency !== 0 && dividend.ex_dividend_date)
    .sort((left, right) => compareExDividendDates(right, left));

const getYearOverYearGrowth = (dividends: Dividend[]) => {
  const dividendFrequency = dividends.find((dividend: Dividend) => dividend.frequency !== 0)?.frequency;

  if (!dividendFrequency) {
    return null;
  }

  const completedDividends = getCompletedDividendsByExDate(dividends).slice(0, dividendFrequency * 2);

  if (completedDividends.length < dividendFrequency * 2) {
    return null;
  }

  const yearOverYearDividends = completedDividends.reduce(
    (acc, curr, index) => {
      const dividend = curr.dividend;
      if (index < dividendFrequency) {
        acc.lastYear += Number(dividend);
      } else {
        acc.trailingYear += Number(dividend);
      }
      return acc;
    },
    { lastYear: 0, trailingYear: 0 },
  );

  const yearOverYearGrowth =
    (yearOverYearDividends.lastYear / dividendFrequency - yearOverYearDividends.trailingYear / dividendFrequency) /
    (yearOverYearDividends.trailingYear / dividendFrequency);

  if (!yearOverYearGrowth) {
    return null;
  }

  return yearOverYearGrowth * 100;
};

const get5YearAvgDividendGrowth = (dividends: Dividend[]) => {
  const completedDividends = getCompletedDividendsByExDate(dividends);
  const currentDividend = completedDividends[0];

  if (!currentDividend?.ex_dividend_date) {
    return null;
  }

  const currentDate = DateTime.fromISO(currentDividend.ex_dividend_date);

  const lastDividend = completedDividends.find(d => {
    if (!d.ex_dividend_date) {
      return false;
    }
    const dividendYear = DateTime.fromISO(d.ex_dividend_date).year;
    const dividendMonth = DateTime.fromISO(d.ex_dividend_date).month;
    if (dividendYear === currentDate.year - 5 && dividendMonth === currentDate.month) {
      return true;
    }
    return false;
  });

  if (lastDividend === undefined) {
    return null;
  }

  const dividendGrowth = (Number(currentDividend.dividend) / Number(lastDividend.dividend)) ** (1 / 5) - 1;
  return dividendGrowth * 100;
};

const getLargestDividendYield = (dividends: Dividend[]) => {
  const sortedDividends = [...dividends].sort((a, b) => Number(b.dividend_yield) - Number(a.dividend_yield));
  return sortedDividends[0];
};

const getDividendDetailedData = (dividends: Dividend[]): DividendSummary => {
  return {
    avgAnnualizedDividendGrowth: get5YearAvgDividendGrowth(dividends) ?? null,
    futureDividends: getFutureDividends(dividends) ?? [],
    dividends: dividends ?? [],
    largestDividendYield: getLargestDividendYield(dividends) ?? null,
    last: getLastDividendInfo(dividends),
    next: getNextDividendInfo(dividends),
    yearOverYearGrowth: getYearOverYearGrowth(dividends) ?? null,
  };
};

export const getDividendSummary = async (symbol: string, pageSize?: number): Promise<DividendSummary> => {
  const session = getGlobalSession();
  const dividendRes = await fetchDividends(symbol, session, pageSize);
  const dividends = dividendRes?.ok ?? [];
  return await getDividendDetailedData(dividends);
};

// ==================== Earnings ====================

export interface EarningsSummary {
  next: Earnings | null;
  last: Earnings | null;
  year: Earnings | null;
  earnings: Earnings[];
}

const getFutureEarnings = (earnings: Earnings[]): Earnings[] => {
  const futureEarings = earnings?.filter((earning: Earnings) => !isPast(parseISO(earning.date)));
  return (sortByField(futureEarings, 'date')?.reverse() as Earnings[]) ?? [];
};

const getPastEarnings = (earnings: Earnings[]): Earnings[] => {
  return earnings?.filter((earning: Earnings) => isPast(parseISO(earning.date))) ?? [];
};

const getFiscalYear = (earnings: Earnings[]) => {
  return earnings?.filter((earning: Earnings) => earning.period === 'FY') ?? [];
};

export const fetchEarnings = async (symbol: string, session: Session, pageSize?: number) => {
  const dateTo = DateTime.fromJSDate(new Date()).plus({ months: 3 }).toJSDate();
  const dateFrom = DateTime.fromJSDate(new Date())
    .minus({ months: 12 * 5 })
    .toJSDate();

  const params = {
    dateFrom,
    dateTo,
    pageSize,
    symbols: [symbol],
  };

  const calendarManager = session.getManager(CalendarManager);
  return calendarManager.getCalendarData('earnings', params, true);
};

const getEarningsDetailedData = (earnings: Earnings[]): EarningsSummary => {
  const futureEarnings = getFutureEarnings(earnings);
  const pastEarnings = getPastEarnings(earnings);
  const fiscalYear = getFiscalYear(earnings);
  return {
    earnings: earnings ?? [],
    last: pastEarnings?.[0] ?? null,
    next: futureEarnings?.[0] ?? null,
    year: fiscalYear?.[0] ?? null,
  };
};

export const getEarningsSummary = async (symbol: string, pageSize?: number): Promise<EarningsSummary> => {
  const session = getGlobalSession();
  const earningRes = await fetchEarnings(symbol, session, pageSize);
  const earnings = earningRes.ok ?? [];
  return await getEarningsDetailedData(earnings);
};

// ==================== Guidance ====================

export interface GuidanceSummary {
  next: Guidance | null;
  last: Guidance | null;
  guidance: Guidance[];
}

export const fetchGuidance = async (symbol: string, session: Session, pageSize?: number) => {
  const dateTo = DateTime.fromJSDate(new Date()).plus({ months: 6 }).toJSDate();
  const dateFrom = DateTime.fromJSDate(new Date())
    .minus({ months: 12 * 4 })
    .toJSDate();

  const params = {
    dateFrom,
    dateTo,
    pageSize,
    symbols: [symbol],
  };

  const calendarManager = session.getManager(CalendarManager);
  return calendarManager.getCalendarData('guidance', params, true);
};

const getNextGuidance = (guidance: Guidance[]): Guidance | null => {
  const futureGuidance = guidance?.filter((g: Guidance) => !isPast(parseISO(g.date)));
  const sorted = sortByField(futureGuidance, 'date')?.reverse() as Guidance[];
  return sorted?.[0] ?? null;
};

const getLastGuidance = (guidance: Guidance[]): Guidance | null => {
  const pastGuidance = guidance?.filter((g: Guidance) => isPast(parseISO(g.date)));
  return pastGuidance?.[0] ?? null;
};

const getGuidanceDetailedData = (guidance: Guidance[]): GuidanceSummary => {
  return {
    guidance: guidance ?? [],
    last: getLastGuidance(guidance),
    next: getNextGuidance(guidance),
  };
};

export const getGuidanceSummary = async (symbol: string, pageSize?: number): Promise<GuidanceSummary> => {
  const session = getGlobalSession();
  const guidanceRes = await fetchGuidance(symbol, session, pageSize);
  const guidance = guidanceRes.ok ?? [];
  return await getGuidanceDetailedData(guidance);
};

// ==================== Conference Calls ====================

export interface ConferenceCallsSummary {
  next: Conference;
  last: Conference;
  conferenceCalls: Conference[];
}

const getFutureConferenceCalls = (conferenceCalls: Conference[]): Conference[] => {
  const futureConferenceCalls = conferenceCalls?.filter(
    (conferenceCall: Conference) => !isPast(parseISO(conferenceCall.date)),
  );
  return sortByField(futureConferenceCalls, 'date')?.reverse() as Conference[];
};

const getPastConferenceCalls = (conferenceCalls: Conference[]): Conference[] => {
  return conferenceCalls?.filter((conferenceCall: Conference) => isPast(parseISO(conferenceCall.date)));
};

export const fetchConferenceCalls = (symbol: string, session: Session) => {
  const dateTo = DateTime.fromJSDate(new Date()).plus({ months: 3 }).toJSDate();
  const dateFrom = DateTime.fromJSDate(dateTo).minus({ months: 6 }).toJSDate();

  const params = {
    dateFrom,
    dateTo,
    symbols: [symbol],
  };

  const calendarManager = session.getManager(CalendarManager);
  return calendarManager.getCalendarData('conference', params, true);
};

const getConferenceCallsDetailedData = (conferenceCalls: Conference[]): ConferenceCallsSummary => {
  const futureConferenceCalls = getFutureConferenceCalls(conferenceCalls);
  const pastConferenceCalls = getPastConferenceCalls(conferenceCalls);
  return {
    conferenceCalls,
    last: pastConferenceCalls[0] ?? null,
    next: futureConferenceCalls[0] ?? null,
  };
};

export const getConferenceCallsSummary = async (symbol: string): Promise<ConferenceCallsSummary> => {
  const session = getGlobalSession();
  const conferenceCallRes = await fetchConferenceCalls(symbol, session);
  const conferenceCalls = conferenceCallRes.ok ?? [];
  return await getConferenceCallsDetailedData(conferenceCalls);
};

// ==================== Stock Splits ====================

export interface StockSplitsSummary {
  next: Splits | null;
  last: Splits | null;
  splits: Splits[];
}

const getFutureSplits = (splits: Splits[]) => {
  return splits?.filter((split: Splits) => !isPast(parseISO(split.date_ex)));
};

const getPastSplits = (splits: Splits[]) => {
  return splits?.filter((split: Splits) => isPast(parseISO(split.date_ex)));
};

export const fetchStockSplits = async (symbol: string, session: Session) => {
  const dateTo = DateTime.fromJSDate(new Date()).plus({ months: 3 }).toJSDate();
  const dateFrom = DateTime.fromJSDate(new Date()).minus({ months: 1 }).toJSDate();

  const params = {
    dateFrom,
    dateSearchField: 'ex',
    dateTo,
    symbols: [symbol],
  };

  const calendarManager = session.getManager(CalendarManager);
  return calendarManager.getCalendarData('splits', params);
};

const getStockSplitsDetailedData = (splits: Splits[]): StockSplitsSummary => {
  const futureSplits = getFutureSplits(splits);
  const pastSplits = getPastSplits(splits);
  return {
    last: pastSplits[0] ?? null,
    next: futureSplits[0] ?? null,
    splits,
  };
};

export const getStockSplitsSummary = async (symbol: string): Promise<StockSplitsSummary> => {
  const session = getGlobalSession();
  const splitsRes = await fetchStockSplits(symbol, session);
  const splits = splitsRes.ok ?? [];
  return getStockSplitsDetailedData(splits);
};

// ==================== Ratings ====================

const fetchAnalystRatings = (symbol: string, session: Session, pageSize = 100): SafePromise<any[] | undefined> => {
  const dateFrom = DateTime.fromJSDate(new Date()).minus({ years: 3 }).toJSDate();

  const params: ISetParams = {
    dateFrom,
    fields:
      'fields=id,ticker,analyst_id,ratings_accuracy.smart_score,analyst_name,action_company,action_pt,adjusted_pt_current,adjusted_pt_prior,analyst,analyst_name,currency,date,exchange,id,importance,name,notes,pt_current,pt_prior,rating_current,rating_prior,ticker,time,updated,url,url_calendar,url_news,logo,quote',
    pageSize,
    symbols: [symbol.toUpperCase()],
  };

  const calendarManager = session.getManager(CalendarManager);
  return calendarManager.getCalendarData('ratings', params, true);
};

export const getRatingsSummary = async (symbol: string, pageSize?: number): Promise<AnalystRatingsSummary> => {
  const session = getGlobalSession();
  const ratingRes = await fetchAnalystRatings(symbol, session, pageSize);
  const ratings = ratingRes?.ok ?? [];
  return await getRatingsDetailedData(ratings);
};

// ==================== M&A ====================

export interface MASummary {
  next: MergersAndAcquisitions | null;
  last: MergersAndAcquisitions | null;
  ma: MergersAndAcquisitions[];
}

const getFutureMA = (ma: MergersAndAcquisitions[]) => {
  return ma?.filter((event: MergersAndAcquisitions) => !isPast(parseISO(event.date)));
};

const getPastMA = (ma: MergersAndAcquisitions[]) => {
  return ma?.filter((event: MergersAndAcquisitions) => isPast(parseISO(event.date)));
};

export const fetchMA = async (symbol: string, session: Session) => {
  const dateTo = DateTime.fromJSDate(new Date()).plus({ months: 3 }).toJSDate();
  const dateFrom = DateTime.fromJSDate(dateTo)
    .minus({ months: 12 * 5 })
    .toJSDate();

  const params = {
    dateFrom,
    dateTo,
    symbols: [symbol],
  };

  const calendarManager = session.getManager(CalendarManager);
  return calendarManager.getCalendarData('ma', params, true);
};

const getMADetailedData = (ma: MergersAndAcquisitions[]) => {
  const futureMa = getFutureMA(ma);
  const pastMa = getPastMA(ma);
  const last = pastMa[0] ?? null;
  const next = futureMa[0] ?? null;
  return {
    last,
    ma,
    next,
  };
};

export const getMASummary = async (symbol: string) => {
  const session = getGlobalSession();
  const maRes = await fetchMA(symbol, session);
  const mas = maRes.ok ?? [];
  const formattedMAs = mas
    .filter(ma => ma.deal_type === 'Acquisition' || ma.deal_type === 'Merger' || ma.deal_type === 'Asset Sale')
    .map(ma => {
      return {
        ...ma,
        message: generateMAText(ma),
      };
    });
  return await getMADetailedData(formattedMAs);
};

// ==================== Insider Trades ====================

export interface InsiderTradesSummary {
  lastThirtyDaysFilingsSummary: {
    totalSharesPurchased: number;
    totalSharesPurchasedValue: number;
    totalSharesSold: number;
    totalSharesSoldValue: number;
  } | null;
  filings: InsidersTradeFiling[];
  largestThreeTransactionsFromLastNinetyDays: InsidersTradeFiling[];
}

export interface DateRange {
  date_to: Date;
  date_from: Date;
}

export const fetchInsiderTrades = async (symbol: string, pageLimit = 10000) => {
  const session = getGlobalSession();
  const params = {
    company_ticker: symbol,
    filing_date_end: undefined,
    filing_date_preset: 'custom',
    filing_date_start: undefined,
    group_by: 'filing',
    page_limit: pageLimit,
    sort_by: 'last_filing_date',
    trade_date_preset: 'custom',
    trade_types: ['p', 's'],
  };

  const insiderTradesManager = session.getManager(InsiderTradesManager);
  return insiderTradesManager.getInsiderTrades(params);
};

const getFilingsFromDaysAgo = (insiderTradesFilings: InsidersTradeFiling[], daysAgo: number): InsidersTradeFiling[] => {
  if (!insiderTradesFilings) return [];
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  // TODO: Check if this is the correct date field to use
  return insiderTradesFilings.filter(filing => new Date(filing.signature.date) > date);
};

export const getInsiderTradesDetailedData = (insiderTradesFilings: InsidersTradeFiling[]): InsiderTradesSummary => {
  if (!insiderTradesFilings) {
    return {
      filings: [],
      largestThreeTransactionsFromLastNinetyDays: [],
      lastThirtyDaysFilingsSummary: null,
    };
  }
  const lastThirtyDaysFilings = getFilingsFromDaysAgo(insiderTradesFilings, 30);
  const totalSharesSoldFromLastThirtyDays = lastThirtyDaysFilings.reduce((prevFiling, filing) => {
    return filing.trade_status === 'SELL' ? prevFiling + Math.abs(filing.traded_shares) : prevFiling;
  }, 0);
  const totalSharesSoldValueFromLastThirtyDays = lastThirtyDaysFilings.reduce(
    (prevFiling, filing) => (filing.trade_status === 'SELL' ? prevFiling + Math.abs(filing.traded_value) : prevFiling),
    0,
  );
  const totalSharesPurchasedFromLastThirtyDays = lastThirtyDaysFilings.reduce((prevFiling, filing) => {
    return filing.trade_status === 'BUY' ? prevFiling + Math.abs(filing.traded_shares) : prevFiling;
  }, 0);
  const totalSharesPurchasedValueFromLastThirtyDays = lastThirtyDaysFilings.reduce((prevFiling, filing) => {
    return filing.trade_status === 'BUY' ? prevFiling + Math.abs(filing.traded_value) : prevFiling;
  }, 0);

  const lastNinetyDaysFilings = getFilingsFromDaysAgo(insiderTradesFilings, 90);
  const largestThreeTransactionsFromLastNinetyDays = lastNinetyDaysFilings
    .filter(filing => filing.trade_status === 'BUY' || filing.trade_status === 'SELL')
    .sort((a, b) => {
      return b.traded_value - a.traded_value;
    })
    .slice(0, 3);

  return {
    filings: insiderTradesFilings,
    largestThreeTransactionsFromLastNinetyDays,
    lastThirtyDaysFilingsSummary:
      lastThirtyDaysFilings.length > 0
        ? {
            totalSharesPurchased: totalSharesPurchasedFromLastThirtyDays,
            totalSharesPurchasedValue: totalSharesPurchasedValueFromLastThirtyDays,
            totalSharesSold: totalSharesSoldFromLastThirtyDays,
            totalSharesSoldValue: totalSharesSoldValueFromLastThirtyDays,
          }
        : null,
  };
};

export const getInsiderTradesSummary = async (
  symbol: string,
  pageLimit?: number,
): Promise<InsiderTradesSummary | null> => {
  const insiderTradesRes = await fetchInsiderTrades(symbol, pageLimit);
  const insiderTradesResults = insiderTradesRes.ok;
  if (!insiderTradesResults) return null;
  const insiderTradesFilings = insiderTradesResults?.filings?.filings;
  return getInsiderTradesDetailedData(insiderTradesFilings);
};

export const getOptionChain = async (symbols: string | string[]): SafePromise<OrganizeOptionDataReturn> => {
  const session = getGlobalSession();
  const optionChainManager = session.getManager(OptionChainManager);
  const optionChainRes = await optionChainManager.getOptionChainData(symbols);
  if (optionChainRes.err) {
    return { err: optionChainRes.err };
  } else {
    return { ok: organizeOptionData(optionChainRes.ok) };
  }
};
